import {
  CameraIcon as AddPhotosIcon,
  TrashIcon as DeleteOutlineOutlined,
  ArrowDownTrayIcon as DownloadIcon,
} from '@heroicons/react/24/outline'
import { ApiErrorResponse, ApiResponse } from 'apisauce'
import { inject } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router'
import GalleryView from '../../components/GalleryView'
import DropZoneWrapper from '../../components/DropZoneWrapper/DropZoneWrapper.component'

import { SectionHeader } from '../../components/SectionHeader/SectionHeader'
import { TwButton } from '../../components/Button/TwButton'
import { ConfirmModal } from '../../components/ConfirmModal'
import { DropdownMenu, DropdownMenuItem } from '../../components/DropdownMenu'
import {
  FiltersButton,
  FiltersDrawer,
  useGalleryFilters,
  dateFromToApiDate,
  dateToToApiDate,
} from '../../components/FiltersDrawer'
import { GallerySort, useGallerySort } from '../../components/GallerySort'
import AlbumModel from '../../models/album'
import { API, ApiError } from '../../services/api'
import { USER_STORE, WORKSPACES_STORE } from '../../stores'
import { UserStore } from '../../stores/UserModel'
import { downloadFile } from '../../utils/file'
import { WorkspacesStore } from '../../stores/WorkspaceStore'

interface AlbumScreenParams {
  albumId: string
  workspaceSlug: string
}

interface AlbumScreenProps extends RouteComponentProps<AlbumScreenParams> {
  [USER_STORE]: UserStore
  [WORKSPACES_STORE]: WorkspacesStore
}

const AlbumScreen: React.FC<AlbumScreenProps> = (props) => {
  const { albumId } = props.match.params
  const workspacesStore = props[WORKSPACES_STORE]
  const workspaceId = workspacesStore.activeWorkspaceId
  const workspaceSlug = workspacesStore.activeWorkspaceSlug

  const [album, setAlbum] = useState<AlbumModel>(undefined)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const { filters, activeCount, apply, clearAll } = useGalleryFilters(props.location, props.history)
  const { sort, apply: applySort } = useGallerySort(props.location, props.history, {
    hasAlbum: true,
  })

  const fetchAlbum = (id: string) =>
    API.getInstance().getAlbum({ workspaceId, id })

  const onError = (err: Error | ApiErrorResponse<ApiError>) => {
    // TODO: handle error
    props.history.replace(`/${workspaceSlug}`)
  }

  useEffect(() => {
    fetchAlbum(albumId)
      .then((response: ApiResponse<AlbumModel, ApiError>) => {
        if (response.ok) setAlbum(response.data)
        else onError(response as ApiErrorResponse<ApiError>)
      })
      .catch(onError)
  }, [])

  const handleDeleteAlbum = async () => {
    try {
      await API.getInstance().deleteAlbum({
        id: album.id,
        workspaceId,
      })
    } catch (err) {
      // TODO: log to firebase crashlytics
      console.log('Error during album deletion', err)
    } finally {
      setShowDeleteModal(false)
      props.history.replace(`/${workspaceSlug}/albums`)
    }
  }

  // TODO: Handle user roles
  const hasWriteAccess = true

  const handleDownloadAlbum = async () => {
    const response = await API.getInstance().getAlbumDownloadUrl({
      workspaceId,
      id: albumId,
    })
    if (response.ok) {
      downloadFile(response.data.url, response.data.filename)
    }
  }

  const mobileMenuItems: DropdownMenuItem[] = [
    {
      label: 'Pobierz album',
      icon: <DownloadIcon className="h-5 w-5" />,
      onClick: handleDownloadAlbum,
    },
    ...(hasWriteAccess ? [{
      label: 'Usuń album',
      icon: <DeleteOutlineOutlined className="h-5 w-5" />,
      onClick: () => setShowDeleteModal(true),
      variant: 'danger' as const,
    }] : []),
  ]

  return (
    <DropZoneWrapper
      workspaceId={workspaceId}
      workspaceSlug={workspaceSlug}
      history={props.history}
      albumId={albumId}
      album={album}
    >
      <GalleryView
        albumId={albumId}
        orderBy={sort.orderBy}
        order={sort.order}
        query={filters.query}
        personIds={filters.personIds}
        ownerIds={filters.ownerIds}
        deviceId={filters.deviceId}
        type={filters.type}
        dateFrom={filters.dateFrom ? dateFromToApiDate(filters.dateFrom) : undefined}
        dateTo={filters.dateTo ? dateToToApiDate(filters.dateTo) : undefined}
        history={props.history}
        match={props.match}
        location={props.location}
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
      >
        <SectionHeader
        title={album?.name ?? ''}
        description={album?.description ?? ''}
        actions={
          <>
            {/* Add photos button - always visible, leftmost */}
            {hasWriteAccess && (
              <TwButton
                icon={<AddPhotosIcon className="!h-4 !w-4" />}
                onClick={() => props.history.push(`/${workspaceSlug}/upload`)}
              >
                Dodaj zdjęcia
              </TwButton>
            )}

            {/* Desktop buttons - hidden on small mobile */}
            <TwButton
              variant="outline"
              icon={<DownloadIcon className="!h-4 !w-4" />}
              onClick={handleDownloadAlbum}
              className="hidden sm:inline-flex"
            >
              Pobierz album
            </TwButton>
            {hasWriteAccess && (
              <TwButton
                variant="outline"
                color="danger"
                icon={<DeleteOutlineOutlined className="!h-4 !w-4" />}
                onClick={() => setShowDeleteModal(true)}
                className="hidden sm:inline-flex"
              >
                Usuń
              </TwButton>
            )}

            <div className="ml-auto flex items-center gap-2">
              {!filters.query && (
                <GallerySort sort={sort} onChange={applySort} allowAddedToAlbum />
              )}
              <FiltersButton
                activeCount={activeCount}
                onClick={() => setFiltersOpen(true)}
                onClear={clearAll}
              />
              {/* Mobile dropdown menu */}
              <DropdownMenu items={mobileMenuItems} align="right" className="sm:hidden" />
            </div>
          </>
        }
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAlbum}
        title="Usuń album"
        description={`Czy na pewno chcesz usunąć album "${album?.name}"? Zdjęcia nie zostaną usunięte wraz z albumem.`}
        confirmLabel="Usuń album"
        cancelLabel="Anuluj"
        variant="danger"
      />

      </GalleryView>
      <FiltersDrawer
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        workspaceId={workspaceId}
        filters={filters}
        onApply={apply}
        hideAlbumField
      />
    </DropZoneWrapper>
  )
}

export default inject(WORKSPACES_STORE)(inject(USER_STORE)(AlbumScreen))
